import _get from 'lodash/get'
import { print } from 'graphql'
import { post as apiPost, TYPE_GRAPHQL, getGraphqlUrl } from '../../../api-client/index'
import { createAuthForRequest } from '../../auth/index'
import {
  SEARCH_QUERY,
  SEARCH_TILE_HYDRATION_QUERY,
} from './ai-search-queries'
import { tileKey } from './search-tile-hydration'
import type { OracleContentEntity, OracleContentResult } from './useOracleSearch'
import type { AiSearchContentData } from './search-stream-types'

export const SEARCH_PAGE_RESULTS_HYDRATE_KEY = 'searchPageResults'

export const SEARCH_PAGE_CONTENT_LIMIT = 16

const SEARCH_IMAGE_TYPES = [
  'd6-keyart-with-text',
  'd6-keyart-no-text',
  'd6-poster-image',
]

const SSR_SEARCH_TIMEOUT_MS = 4500

/**
 * Variables must match useAiSearchStream executeSearch content query (offset 0).
 */
export function buildSearchPageContentVariables (
  language: string,
  query: string,
) {
  return {
    query,
    language,
    contentLimit: SEARCH_PAGE_CONTENT_LIMIT,
    contentOffset: 0,
    skipOverview: true,
  }
}

/**
 * Variables must match hydrateContentTiles SEARCH_TILE_HYDRATION_QUERY.
 */
export function buildSearchTileHydrationVariables (
  language: string,
  titleGroupIds: string[],
) {
  return {
    titleGroupIds,
    language: [language, 'en', '_primary'],
    imageType: SEARCH_IMAGE_TYPES,
    learnerLevelLanguage: language,
  }
}

export interface SearchPageResultsHydrateSeed {
  query: string
  language: string
  search: {
    apollo: {
      query: 'Search'
      variables: Record<string, unknown>
      data: unknown
      partialErrors: boolean
    }
  }
  tileHydration?: {
    apollo: {
      query: 'SearchTileHydration'
      variables: Record<string, unknown>
      data: unknown
      partialErrors: boolean
    }
  }
  content: OracleContentResult[]
  contentTotalCount: number
  chatSessionId: string | null
  blacklistMessage: string | null
  heroImageUrls: (string | null)[]
}

export interface FetchSearchPageResultsOptions {
  query: string
  language: string
  auth?: unknown
}

function mapHydratedTiles (
  resultIds: string[],
  contentItems: OracleContentEntity[],
): OracleContentResult[] {
  const entityMap = new Map<string, OracleContentEntity>()
  contentItems.forEach((c) => {
    if (c.contentId) {
      entityMap.set(tileKey(c), c)
    }
  })
  return resultIds
    .map((id) => entityMap.get(id))
    .filter((entity): entity is OracleContentEntity => !!entity)
    .map((entity) => ({
      contentId: entity.contentId,
      brooklynId: '',
      contentType: entity.contentType || 'video',
      title: entity.title || '',
      score: 0,
      content: entity,
    }))
}

function isRecord (value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/**
 * Read a one-shot SSR search seed from hydrate when the query matches.
 */
export interface AiSearchSsrBootstrap {
  state: 'RESULTS' | 'STREAMING'
  statusKey: string | null
  results: { content: OracleContentResult[], playlists: [] }
  contentTotalCount: number
  blacklistMessage: string | null
  heroImageUrls: (string | null)[]
  chatSessionId: string | null
  contentOffset: number
  lastQuery: string
}

/**
 * Map an SSR search hydrate seed into useAiSearchStream's initial state so the
 * first client render matches hydrated tiles (no "no results" flash).
 */
export function buildAiSearchBootstrapFromSsrSeed (
  seed: SearchPageResultsHydrateSeed,
  skipAiSearchBrowseSummary = false,
): AiSearchSsrBootstrap {
  const sessionId = seed.chatSessionId || null
  const startOverview = !skipAiSearchBrowseSummary && !!sessionId
  return {
    state: startOverview ? 'STREAMING' : 'RESULTS',
    statusKey: startOverview ? 'generating' : null,
    results: { content: seed.content, playlists: [] },
    contentTotalCount: seed.contentTotalCount,
    blacklistMessage: seed.blacklistMessage,
    heroImageUrls: seed.heroImageUrls || [],
    chatSessionId: sessionId,
    contentOffset: seed.content.length,
    lastQuery: seed.query,
  }
}

export function pickSearchPageSsrSeed (
  hydrate: unknown,
  query: string,
): SearchPageResultsHydrateSeed | null {
  if (!query || !isRecord(hydrate)) {
    return null
  }
  const seed = hydrate[SEARCH_PAGE_RESULTS_HYDRATE_KEY]
  if (!isRecord(seed) || seed.query !== query) {
    return null
  }
  if (!Array.isArray(seed.content)) {
    return null
  }
  return seed as unknown as SearchPageResultsHydrateSeed
}

/**
 * SSR first-paint search tiles for /search?q=…. Fetches the content search
 * query (skipOverview — no LLM stream) then hydrates tiles via contentList.
 * Best-effort: returns null on timeout or empty query.
 */
export async function fetchSearchPageResults (
  options: FetchSearchPageResultsOptions,
): Promise<SearchPageResultsHydrateSeed | null> {
  const { query, language, auth } = options
  const trimmed = String(query || '').trim()
  if (!trimmed) {
    return null
  }

  const graphqlUrl = getGraphqlUrl()
  const searchVariables = buildSearchPageContentVariables(language, trimmed)
  const authOptions = {
    auth: createAuthForRequest({ auth: auth as never, includeJwt: true }),
    timeout: SSR_SEARCH_TIMEOUT_MS,
    maxAttempts: 1,
  } as never

  try {
    const searchRes = await apiPost(
      graphqlUrl,
      { query: print(SEARCH_QUERY), variables: searchVariables },
      authOptions,
      TYPE_GRAPHQL,
    )
    const searchErrors = _get(searchRes, ['body', 'errors'], [])
    const searchData = _get(searchRes, ['body', 'data', 'search']) as AiSearchContentData | undefined
    if (!searchData) {
      return null
    }

    const resultIds = searchData.resultIds || []
    const searchApolloData = _get(searchRes, ['body', 'data'], null)

    let tileHydrationSeed: SearchPageResultsHydrateSeed['tileHydration']
    let content: OracleContentResult[] = []

    if (resultIds.length > 0) {
      const tileVariables = buildSearchTileHydrationVariables(language, resultIds)
      const tileRes = await apiPost(
        graphqlUrl,
        { query: print(SEARCH_TILE_HYDRATION_QUERY), variables: tileVariables },
        authOptions,
        TYPE_GRAPHQL,
      )
      const tileErrors = _get(tileRes, ['body', 'errors'], [])
      const tileItems = _get(tileRes, ['body', 'data', 'contentList', 'content'], []) as OracleContentEntity[]
      content = mapHydratedTiles(resultIds, tileItems)
      tileHydrationSeed = {
        apollo: {
          query: 'SearchTileHydration',
          variables: tileVariables,
          data: _get(tileRes, ['body', 'data'], null),
          partialErrors: tileErrors.length > 0,
        },
      }
    }

    if (content.length === 0 && !searchData.blacklistMessage) {
      return null
    }

    return {
      query: trimmed,
      language,
      search: {
        apollo: {
          query: 'Search',
          variables: searchVariables,
          data: searchApolloData,
          partialErrors: searchErrors.length > 0,
        },
      },
      tileHydration: tileHydrationSeed,
      content,
      contentTotalCount: searchData.contentTotalCount || content.length,
      chatSessionId: searchData.chatSessionId || null,
      blacklistMessage: searchData.blacklistMessage || null,
      heroImageUrls: searchData.heroImageUrls || [],
    }
  } catch {
    return null
  }
}
